import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { callErrorToast } from "../../../utilities/utilities.ts";
import { ErrorResponse } from "../../../Models/Error.ts";
import { KpiService } from "../../../services/shipment/kpi.ts";
import {
  KpiShipmentPerformanceFilter,
  KpiShipmentPerformance,
} from "../../../Models/KpiShipmentPerformance.ts";

const kpiService = new KpiService();

interface ShipmentsPerformancesDelayedDaysGroupedProviderProps {
  children: ReactNode;
  filters: KpiShipmentPerformanceFilter;
}

interface ShipmentsPerformancesDelayedDaysGroupedContextValue {
  data: KpiShipmentPerformance[];
  loader: boolean;
  filters: KpiShipmentPerformanceFilter;
}

const ShipmentsPerformancesDelayedDaysGroupedContext = createContext<
  ShipmentsPerformancesDelayedDaysGroupedContextValue | undefined
>(undefined);

const ShipmentsPerformancesDelayedDaysGroupedProvider: React.FC<
  ShipmentsPerformancesDelayedDaysGroupedProviderProps
> = ({ children, filters }) => {
  const [data, setData] = useState<KpiShipmentPerformance[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiShipmentPerformance> | null>(null);

  const prevFilters = useRef<KpiShipmentPerformanceFilter>(filters);

  const getPerformancesDelayedDaysGrouped = (): void => {
    setLoader(true);
    kpiService
      .getPerformancesDelayedDaysGrouped(filters)
      .then((res) => {
        setLoader(false);
        setData(res?.data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getPerformancesDelayedDaysGrouped();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getPerformancesDelayedDaysGrouped();
  }, []);

  return (
    <ShipmentsPerformancesDelayedDaysGroupedContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </ShipmentsPerformancesDelayedDaysGroupedContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentsPerformancesDelayedDaysGroupedContext.
 * Ensures that the context is used only within a ShipmentsPerformancesDelayedDaysGroupedProvider.
 */
const useShipmentsPerformancesDelayedDaysGroupedContext =
  (): ShipmentsPerformancesDelayedDaysGroupedContextValue => {
    const context = useContext(ShipmentsPerformancesDelayedDaysGroupedContext);
    if (!context) {
      throw new Error(
        "useShipmentsPerformancesDelayedDaysGroupedContext must be used within a ShipmentsPerformancesDelayedDaysGroupedProvider"
      );
    }
    return context;
  };

// Export the provider and custom hook for use in other components
export {
  ShipmentsPerformancesDelayedDaysGroupedProvider,
  useShipmentsPerformancesDelayedDaysGroupedContext,
};
